import React, { useEffect, useState } from 'react';

import {
  ExclamationCircleOutlined,
  EyeOutlined,
  FilterOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  App,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Form,
  MenuProps,
  Popover,
  Row,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApiErrorData } from 'models/ApiResponse';
import {
  RequirementListItem,
  RequirementStatus,
  RequirementsListParams,
  RequirementsListResponse,
  TouchLatamTask,
} from 'models/Requirement';

import { RequirementService } from 'services/RequirementService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../components/Layout/Layout';
import appI18n from '../../config/i18n';
import CreateRequirementModal from './Components/CreateRequirementModal';

import './Requirements.scss';

type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PAGE_SIZE_STORAGE_KEY = 'requirements.pageSize';
const DEFAULT_COUNTRY = 'CL';
const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];

const STATUS_COLOR: Record<RequirementStatus, string> = {
  open: 'green',
  closed: 'default',
};

// Cache de campañas por país (taskId -> nombre), persistente entre montajes
// del componente para evitar recargas y parpadeos al volver a un país.
const campaignCache: Record<string, Record<number, string>> = {};

const getInitialPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch (e) {
    // localStorage puede no estar disponible
  }
  return PAGE_SIZE;
};

interface Props {
  canCreate?: boolean;
  basePath: string;
}

function Requirements({ canCreate = false, basePath }: Props) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { message, modal } = App.useApp();
  const navigate = useNavigate();

  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [dataSource, setDataSource] = useState<RequirementListItem[]>([]);
  // Mapa taskId -> nombre de campaña, según el país del filtro (cacheado).
  const [taskMap, setTaskMap] = useState<Record<number, string>>(
    () => campaignCache[DEFAULT_COUNTRY] || {}
  );
  const [campaignsReady, setCampaignsReady] = useState<boolean>(
    () => !!campaignCache[DEFAULT_COUNTRY]
  );

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(getInitialPageSize);
  const [total, setTotal] = useState<number>(0);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Filtros aplicados (los que se mandan al backend)
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [term, setTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<
    RequirementStatus | undefined
  >(undefined);

  // Filtros en borrador (los que se editan dentro del popover)
  const [draftCountry, setDraftCountry] = useState<string>(DEFAULT_COUNTRY);
  const [draftStatus, setDraftStatus] = useState<RequirementStatus | undefined>(
    undefined
  );

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setPage(1);
  }, [country, statusFilter, term]);

  useEffect(() => {
    fetchData();
  }, [page, pageSize, sortDir, country, statusFilter, term]);

  useEffect(() => {
    loadCampaigns(country);
  }, [country]);

  const fetchData = async (silent = false) => {
    if (!silent) {
      setTableLoading(true);
    }

    const params: RequirementsListParams = {
      country,
      page,
      pageSize,
      sort: 'createdAt',
      dir: sortDir,
    };
    if (statusFilter) params.status = statusFilter;
    if (term) params.term = term;

    const response = await RequirementService.getRequirements(params);

    if (response.success) {
      const data = response.data as RequirementsListResponse;
      setDataSource(data.data);
      setTotal(data.totalRows);
    } else if (!silent) {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.scene.errors.fetch_list'),
        3.5
      );
    }

    if (!silent) {
      setTableLoading(false);
    }
  };

  // Trae (y cachea) el catálogo de campañas del país para resolver
  // taskId -> nombre. Si ya está cacheado se usa al instante; si no, se marca
  // como no-listo para que la grilla espere y muestre todo junto.
  const loadCampaigns = async (countryCode: string) => {
    const cached = campaignCache[countryCode];
    if (cached) {
      setTaskMap(cached);
      setCampaignsReady(true);
      return;
    }
    setCampaignsReady(false);
    const response = await RequirementService.getTasks(countryCode);
    if (response.success) {
      const tasks = response.data as TouchLatamTask[];
      const map: Record<number, string> = {};
      tasks.forEach((task) => {
        map[task.id] = task.name;
      });
      campaignCache[countryCode] = map;
      setTaskMap(map);
    }
    setCampaignsReady(true);
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const handleTableChange: TableProps<RequirementListItem>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
      try {
        localStorage.setItem(
          PAGE_SIZE_STORAGE_KEY,
          String(pagination.pageSize)
        );
      } catch (e) {
        // localStorage puede no estar disponible
      }
    }

    const sorterLocal = sorter as any;
    const order = sorterLocal?.order as 'ascend' | 'descend' | undefined;
    if (order) {
      setSortDir(order === 'descend' ? 'desc' : 'asc');
    } else {
      setSortDir('desc');
    }
  };

  const showDetail = (row: RequirementListItem) => {
    navigate(`${basePath}/${row.id}`);
  };

  const handleToggleStatus = (row: RequirementListItem) => {
    const newStatus: RequirementStatus =
      row.status === 'open' ? 'closed' : 'open';
    const isClosing = newStatus === 'closed';
    const key = isClosing ? 'close' : 'open';

    modal.confirm({
      title: t(`scenes.requirements.scene.status_confirm.title_${key}`),
      icon: <ExclamationCircleOutlined />,
      content: t(`scenes.requirements.scene.status_confirm.content_${key}`, {
        name: row.name,
      }),
      okText: t(`scenes.requirements.scene.status_confirm.ok_${key}`),
      cancelText: t('scenes.requirements.scene.status_confirm.cancel'),
      okButtonProps: { danger: isClosing },
      onOk: async () => {
        const response = await RequirementService.updateStatus(row.id, {
          status: newStatus,
        });
        if (response.success) {
          message.success(
            t(`scenes.requirements.scene.status_confirm.success_${key}`),
            2.5
          );
          setDataSource((prev) =>
            prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r))
          );
        } else {
          const error = response.data as ApiErrorData;
          message.error(
            (error.message as string) ||
              t('scenes.requirements.scene.status_confirm.error'),
            3.5
          );
        }
      },
    });
  };

  const handleFiltersOpenChange = (open: boolean) => {
    if (open) {
      setDraftCountry(country);
      setDraftStatus(statusFilter);
    }
    setFiltersOpen(open);
  };

  const handleApplyFilters = () => {
    setCountry(draftCountry);
    setStatusFilter(draftStatus);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setDraftCountry(DEFAULT_COUNTRY);
    setDraftStatus(undefined);
  };

  const activeFiltersCount =
    1 + // país siempre cuenta
    (statusFilter ? 1 : 0);

  const getDropdownMenu = (row: RequirementListItem): MenuProps['items'] => {
    const isOpen = row.status === 'open';
    const items: MenuProps['items'] = [
      {
        key: 'view',
        label: (
          <span>
            <EyeOutlined style={{ marginRight: '8px' }} />
            {t('scenes.requirements.scene.dropdown.view_detail')}
          </span>
        ),
        onClick: () => showDetail(row),
      },
    ];

    if (canCreate) {
      items.push({
        key: 'toggle-status',
        label: (
          <span>
            {isOpen ? (
              <LockOutlined style={{ marginRight: '8px' }} />
            ) : (
              <UnlockOutlined style={{ marginRight: '8px' }} />
            )}
            {t(
              isOpen
                ? 'scenes.requirements.scene.dropdown.close'
                : 'scenes.requirements.scene.dropdown.open'
            )}
          </span>
        ),
        onClick: () => handleToggleStatus(row),
      });
    }

    return items;
  };

  const getCountryOptions = () =>
    COUNTRY_OPTIONS.map((opt) => {
      const code = opt.value.toLowerCase();
      return {
        value: opt.value,
        label: (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <img
              src={`https://flagcdn.com/16x12/${code}.png`}
              srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
              width={16}
              height={12}
              alt=""
              style={{ display: 'block' }}
            />
            <span>
              {t(`scenes.requirements.scene.countries.${opt.value}`, opt.value)}
            </span>
          </span>
        ),
      };
    });

  const statusOptions: { value: RequirementStatus; label: string }[] = [
    {
      value: 'open',
      label: t('scenes.requirements.scene.statuses.open'),
    },
    {
      value: 'closed',
      label: t('scenes.requirements.scene.statuses.closed'),
    },
  ];

  const formatDateCell = (isoDate: string): string => {
    const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
    return dayjs(isoDate).format(`${datePart} h:mm A`);
  };

  const renderStatus = (status: RequirementStatus) => (
    <Tag color={STATUS_COLOR[status]} style={{ marginRight: 0 }}>
      {t(`scenes.requirements.scene.statuses.${status}`)}
    </Tag>
  );

  const filtersContent = (
    <div style={{ width: 320 }}>
      <Form layout="vertical" style={{ marginBottom: 0 }}>
        <Form.Item
          label={t('scenes.requirements.scene.filters.country_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            value={draftCountry}
            onChange={(value) => setDraftCountry(value)}
            options={getCountryOptions()}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.requirements.scene.filters.status_label')}
          style={{ marginBottom: 0 }}
        >
          <Select
            value={draftStatus}
            onChange={(value) =>
              setDraftStatus(value as RequirementStatus | undefined)
            }
            options={statusOptions}
            style={{ width: '100%' }}
            placeholder={t(
              'scenes.requirements.scene.filters.status_placeholder'
            )}
            allowClear
          />
        </Form.Item>
      </Form>
      <Divider style={{ margin: '16px 0 12px' }} />
      <Row justify="space-between" align="middle">
        <Col>
          <Button type="text" onClick={handleClearFilters}>
            {t('scenes.requirements.scene.filters.clear_filters')}
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleApplyFilters}>
            {t('scenes.requirements.scene.filters.apply_filters')}
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <Layout title={''}>
      <div className="requirements-scene">
        <Card>
          <Row gutter={[16, 12]} align="middle" justify="space-between">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.requirements.scene.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.requirements.scene.filters.search_placeholder'
                  )}
                  onSearch={onSearch}
                  disabled={tableLoading}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="none">
              <Space size={8}>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  open={filtersOpen}
                  onOpenChange={handleFiltersOpenChange}
                  content={filtersContent}
                  destroyTooltipOnHide
                >
                  <Badge
                    count={activeFiltersCount}
                    size="small"
                    offset={[-4, 4]}
                  >
                    <Button icon={<FilterOutlined />}>
                      <Space size={6}>
                        {t(
                          'scenes.requirements.scene.filters.advanced_filters'
                        )}
                      </Space>
                    </Button>
                  </Badge>
                </Popover>
                {canCreate && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsCreateModalOpen(true)}
                  >
                    {t('scenes.requirements.scene.create_button')}
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        <Card style={{ marginTop: '12px' }}>
          <Table<RequirementListItem>
            dataSource={dataSource}
            bordered
            loading={tableLoading || !campaignsReady}
            size="small"
            scroll={{ x: 900 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            }}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t('scenes.requirements.scene.table.columns.name')}
              key="name"
              render={(row: RequirementListItem) => (
                <div className="requirement-name-cell">
                  <button
                    type="button"
                    className="requirement-id-link"
                    onClick={() => showDetail(row)}
                  >
                    {row.name}
                  </button>
                  <div className="requirement-name-sub">{row.id}</div>
                </div>
              )}
            />

            <Column
              title={t('scenes.requirements.scene.table.columns.campaign')}
              key="campaign"
              render={(row: RequirementListItem) => (
                <span>{taskMap[row.taskId] || `#${row.taskId}`}</span>
              )}
            />

            <Column
              width={180}
              align="center"
              title={t('scenes.requirements.scene.table.columns.created')}
              key="createdAt"
              sorter
              sortOrder={sortDir === 'asc' ? 'ascend' : 'descend'}
              render={(row: RequirementListItem) => (
                <span>
                  {row.createdAt ? formatDateCell(row.createdAt) : '—'}
                </span>
              )}
            />

            <Column
              width={120}
              align="center"
              title={t('scenes.requirements.scene.table.columns.status')}
              key="status"
              render={(row: RequirementListItem) => renderStatus(row.status)}
            />

            <Column
              width={160}
              align="center"
              title={t('scenes.requirements.scene.table.columns.people')}
              key="people"
              render={(row: RequirementListItem) => (
                <Space size={6}>
                  <span>
                    {row.associatedCount} / {row.referentialCapacity}
                  </span>
                  {row.associatedCount > row.referentialCapacity && (
                    <Tag color="gold" style={{ marginRight: 0 }}>
                      {t('scenes.requirements.scene.table.capacity_exceeded')}
                    </Tag>
                  )}
                </Space>
              )}
            />

            <Column
              width={70}
              align="center"
              title=""
              key="actions"
              render={(row: RequirementListItem) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>
      </div>

      {canCreate && (
        <CreateRequirementModal
          isOpen={isCreateModalOpen}
          defaultCountry={country}
          handleModalCancel={() => setIsCreateModalOpen(false)}
          onSaved={() => {
            message.success(t('scenes.requirements.scene.create.success'), 3);
            fetchData(true);
          }}
        />
      )}
    </Layout>
  );
}

export default Requirements;
